import { Loader2, Pencil, Plus, Trash2, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  ArchetypeSelector,
  PhotoUpload,
  VideoUpload,
  VoiceRecorder,
} from "@/components/common";
import { Button, Input, Textarea } from "@/components/ui";
import { CONTENT_KEYWORDS } from "@/constants/content";
import { ROUTES } from "@/constants/routes";
import { AvatarDetailModal } from "@/features/Avatar/AvatarDetailModal";
import { pageViewStorage } from "@/lib/storage/pageView";
import { apiAvatar } from "@/services/api-client/avatar";
import { useExtractTalkingHeadFrames } from "@/services/api-hooks/content";
import { gtmService } from "@/services/gtm-service";
import { trackAvatarsPageView } from "@/services/gtm-service/avatars/trackAvatarsPageView";
import type { Avatar } from "@/types/avatar";

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

export default function Avatars() {
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [archetype, setArchetype] = useState("professional");
  const [customStyle, setCustomStyle] = useState("");
  const [voiceAudio, setVoiceAudio] = useState<string | null>(null);
  const [textPrompt, setTextPrompt] = useState("");
  const [inputMode, setInputMode] = useState<"photo" | "video" | "prompt">("photo");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewAvatar, setPreviewAvatar] = useState<Avatar | null>(null);

  const extractFrames = useExtractTalkingHeadFrames();

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevAvatarsRef = useRef<Avatar[]>([]);

  const syncAvatars = (nextAvatars: Avatar[]) => {
    const prevAvatars = prevAvatarsRef.current;

    for (const nextAvatar of nextAvatars) {
      const prevAvatar = prevAvatars.find((p) => p.id === nextAvatar.id);
      if (prevAvatar?.status !== "generating") continue;

      if (nextAvatar.status === "ready") {
        const gen_time = Math.round(
          (Date.parse(nextAvatar.updated_at) - Date.parse(nextAvatar.created_at)) / 1000,
        );
        gtmService.trackAvatarCreateScs({ avatar_id: nextAvatar.id, gen_time });
      } else if (nextAvatar.status === "failed") {
        gtmService.trackAvatarCreateFail({
          avatar_id: nextAvatar.id,
          stage: "generation",
          error: "generation_failed",
        });
      }
    }
    prevAvatarsRef.current = nextAvatars;
    setAvatars(nextAvatars);
  };

  const load = async () => {
    try {
      const data = await apiAvatar.getAvatars();
      syncAvatars(data);

      // Poll if any avatars are generating
      const hasGenerating = data.some((a) => a.status === "generating");
      if (hasGenerating && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          const fresh = await apiAvatar.getAvatars();
          syncAvatars(fresh);

          if (!fresh.some((a) => a.status === "generating")) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          }
        }, 10000);
      }
    } catch {
      toast.error("Failed to load avatars");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    const view_count = pageViewStorage.incrementViewCount(ROUTES.AVATARS);
    trackAvatarsPageView({ view_count });
  }, []);

  const resetForm = () => {
    setName("");
    setImages([]);
    setVideoFile(null);
    setTextPrompt("");
    setInputMode("photo");
    setArchetype("professional");
    setCustomStyle("");
    setVoiceAudio(null);
    setShowCreate(false);
    setEditingId(null);
  };

  const handleCreate = async () => {
    const validName = name.trim();
    if (!validName) return;
    setSaving(true);
    try {
      const arch =
        archetype === "custom" && customStyle
          ? `${CONTENT_KEYWORDS.CUSTOM_ARCHETYPE} ${customStyle}`
          : archetype;

      // Extract frames from video if in video mode
      let resolvedImage = images[0] ?? null;
      let resolvedExtraImages: string[] | undefined;
      let resolvedVoiceAudio = voiceAudio;
      if (inputMode === "video" && videoFile) {
        toast.loading("Extracting frames from video...", { id: "extract-frames" });
        try {
          const data = await extractFrames.mutateAsync(videoFile);
          const frames: string[] = data.frames ?? [];
          resolvedImage = frames[0] ?? null;
          if (!resolvedImage) throw new Error("No frames extracted from video");
          // Pass remaining frames as extra reference photos
          if (frames.length > 1) resolvedExtraImages = frames.slice(1);
          if (data.audio_url && !resolvedVoiceAudio) {
            resolvedVoiceAudio = data.audio_url;
          }
        } catch (error) {
          const errorMessage = getErrorMessage(
            error,
            "Failed to extract frames from video",
          );
          gtmService.trackAvatarCreateFail({
            stage: "submission",
            error: `video_extraction: ${errorMessage}`,
          });
          toast.error(errorMessage);
          return;
        } finally {
          toast.dismiss("extract-frames");
        }
      }

      const validTextPrompt = textPrompt.trim();

      const imageOrPrompt =
        inputMode === "prompt" ? `prompt:${validTextPrompt}` : resolvedImage!;

      // Optimistic: add placeholder to list immediately
      const placeholder: Avatar = {
        id: `temp-${Date.now()}`,
        name: validName,
        image_url: resolvedImage ?? "",
        archetype: arch,
        status: "generating",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setAvatars((prev) => [placeholder, ...prev]);
      resetForm();
      toast.success("Avatar generation started!");

      gtmService.trackAvatarCreateTry({
        name: validName,
        source: inputMode,
        archetype: archetype,
        arch_prompt: archetype === "custom" ? customStyle : undefined,
        photo_count: inputMode === "photo" ? images.length : undefined,
        text_prompt: inputMode === "prompt" ? validTextPrompt : undefined,
        has_voice: !!voiceAudio,
      });

      // Fire and forget — don't wait for response
      apiAvatar
        .createAvatar({
          name: placeholder.name,
          image: imageOrPrompt,
          images:
            inputMode === "video"
              ? resolvedExtraImages
              : inputMode === "photo" && images.length > 1
                ? images.slice(1)
                : undefined,
          archetype: arch,
          voiceAudio: resolvedVoiceAudio || undefined,
        })
        .then(() => {
          load(); // refresh to get real ID + start polling
        })
        .catch((err: unknown) => {
          const errorMessage = getErrorMessage(err, "Failed to create avatar");
          gtmService.trackAvatarCreateFail({
            stage: "submission",
            error: errorMessage,
          });
          toast.error(errorMessage);
          load();
        });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string) => {
    const validName = name.trim();
    if (!validName) return;

    setSaving(true);
    try {
      const arch =
        archetype === "custom" && customStyle
          ? `${CONTENT_KEYWORDS.CUSTOM_ARCHETYPE} ${customStyle}`
          : archetype;
      await apiAvatar.updateAvatar(id, { name: validName, archetype: arch });
      toast.success("Avatar updated!");
      resetForm();
      load();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, "Failed to update avatar"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    gtmService.trackAvatarDeleteClick({ avatar_id: id });

    if (!confirm("Delete this avatar?")) return;
    try {
      await apiAvatar.deleteAvatar(id);
      toast.success("Avatar deleted");
      load();
    } catch {
      toast.error("Failed to delete avatar");
    }
  };

  const startEdit = (avatar: Avatar) => {
    gtmService.trackAvatarEditClick({ avatar_id: avatar.id });

    setEditingId(avatar.id);
    setName(avatar.name);
    if (avatar.archetype.startsWith(CONTENT_KEYWORDS.CUSTOM_ARCHETYPE)) {
      setArchetype("custom");
      setCustomStyle(
        avatar.archetype.slice(CONTENT_KEYWORDS.CUSTOM_ARCHETYPE.length).trim(),
      );
    } else {
      setArchetype(avatar.archetype);
      setCustomStyle("");
    }
    setShowCreate(false);
  };

  const ARCHETYPE_LABELS: Record<string, string> = {
    motivational_speaker: "Motivational Speaker",
    tech_expert: "Tech Expert",
    storyteller: "Storyteller",
    news_anchor: "News Anchor",
    comedy: "Comedy",
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="text-sm text-zinc-400">Loading avatars...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <div className="mb-6 flex items-center justify-between gap-2 sm:mb-8">
        <div>
          <h1 className="font-bold text-2xl text-zinc-900 sm:text-3xl">Avatars</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Manage your AI personas for video generation
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => {
            resetForm();
            setShowCreate(true);
            gtmService.trackAvatarCreateClick();
          }}
        >
          <Plus className="h-4 w-4" />
          New Avatar
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm sm:p-6">
          <h2 className="mb-4 font-semibold text-lg text-zinc-900">Create Avatar</h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block font-medium text-sm text-zinc-700">Name</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Tech Presenter"
              />
            </div>
            <div className="mb-2 flex flex-wrap gap-2">
              <Button
                variant={inputMode === "photo" ? "default" : "outline"}
                onClick={() => setInputMode("photo")}
                className="flex-1"
              >
                📷 Photo
              </Button>
              <Button
                variant={inputMode === "video" ? "default" : "outline"}
                onClick={() => setInputMode("video")}
                className="flex-1"
              >
                🎥 Video
              </Button>
              <Button
                variant={inputMode === "prompt" ? "default" : "outline"}
                onClick={() => setInputMode("prompt")}
                className="flex-1"
              >
                ✏️ Text Prompt
              </Button>
            </div>
            {inputMode === "photo" ? (
              <PhotoUpload
                label="Photo"
                values={images}
                onChange={setImages}
                hint="Clear, front-facing photo works best"
                onSelect={() => {
                  gtmService.trackAvatarPhotoUploadTry();
                }}
                onSuccess={(photos) => {
                  gtmService.trackAvatarPhotoUploadScs({ photo_count: photos.length });
                }}
                onError={(message) => {
                  gtmService.trackAvatarPhotoUploadFail({ error: message });
                }}
              />
            ) : inputMode === "video" ? (
              <VideoUpload
                value={videoFile}
                onChange={setVideoFile}
                onSelect={() => {
                  gtmService.trackAvatarVideoUploadTry();
                }}
                onSuccess={() => {
                  gtmService.trackAvatarVideoUploadScs();
                }}
                onError={(message) => {
                  gtmService.trackAvatarVideoUploadFail({ error: message });
                }}
              />
            ) : (
              <div>
                <label className="mb-1 block font-medium text-sm text-zinc-700">
                  Describe your character
                </label>
                <Textarea
                  value={textPrompt}
                  onChange={(e) => setTextPrompt(e.target.value)}
                  placeholder="e.g. A young Asian woman with long black hair, confident smile, professional look"
                />
              </div>
            )}
            <ArchetypeSelector
              value={archetype}
              onChange={setArchetype}
              customStyle={customStyle}
              onCustomStyleChange={setCustomStyle}
            />
            {inputMode !== "video" && (
              <VoiceRecorder value={voiceAudio} onChange={setVoiceAudio} />
            )}
            <div className="flex gap-3">
              <Button
                variant="default"
                onClick={handleCreate}
                disabled={
                  !name.trim() ||
                  (inputMode === "photo"
                    ? images.length === 0
                    : inputMode === "video"
                      ? !videoFile
                      : !textPrompt.trim()) ||
                  saving
                }
                className="flex-1"
              >
                {saving ? "Generating avatar..." : "Create Avatar"}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Avatar list */}
      {avatars.length === 0 && !showCreate ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-zinc-200 border-dashed py-16">
          <User className="mb-3 h-12 w-12 text-zinc-300" />
          <p className="text-sm text-zinc-500">No avatars yet</p>
          <Button
            variant="ghost"
            onClick={() => setShowCreate(true)}
            className="mt-2 text-primary hover:bg-background hover:text-primary-hover"
          >
            Create your first avatar
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {avatars.map((avatar) => (
            <div
              key={avatar.id}
              className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm"
            >
              <div
                className="relative h-16 w-16 shrink-0 cursor-pointer"
                onClick={() =>
                  avatar.status === "ready" &&
                  avatar.image_url &&
                  setPreviewAvatar(avatar)
                }
              >
                <img
                  src={avatar.portrait_url || avatar.image_url}
                  alt={avatar.name}
                  className={`h-16 w-16 rounded-xl object-cover ${avatar.status === "generating" ? "opacity-50" : "transition-opacity hover:opacity-80"}`}
                />
                {avatar.status === "generating" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                )}
              </div>
              {editingId === avatar.id ? (
                <div className="flex-1 space-y-2">
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Tech Presenter"
                  />
                  <ArchetypeSelector
                    value={archetype}
                    onChange={setArchetype}
                    customStyle={customStyle}
                    onCustomStyleChange={setCustomStyle}
                  />
                  <div className="flex gap-3">
                    <Button
                      variant="default"
                      onClick={() => handleUpdate(avatar.id)}
                      disabled={saving}
                      className="flex-1"
                    >
                      {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <h3 className="font-medium text-zinc-900">{avatar.name}</h3>
                    <p className="text-sm text-zinc-500">
                      {avatar.archetype.startsWith(CONTENT_KEYWORDS.CUSTOM_ARCHETYPE)
                        ? avatar.archetype
                            .slice(CONTENT_KEYWORDS.CUSTOM_ARCHETYPE.length)
                            .trim()
                        : ARCHETYPE_LABELS[avatar.archetype] || avatar.archetype}
                    </p>
                    {avatar.status === "generating" && (
                      <p className="mt-0.5 font-medium text-primary text-xs">
                        Generating avatar...
                      </p>
                    )}
                    {avatar.status === "failed" && (
                      <p className="mt-0.5 font-medium text-red-500 text-xs">
                        Generation failed
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="icon-sm"
                      variant="outline"
                      onClick={() => startEdit(avatar)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="outline"
                      onClick={() => handleDelete(avatar.id)}
                      className="hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Avatar Detail Modal */}
      {previewAvatar && (
        <AvatarDetailModal
          avatar={previewAvatar}
          onClose={() => setPreviewAvatar(null)}
          onRegenerated={() => {
            setPreviewAvatar(null);
            load();
          }}
        />
      )}
    </div>
  );
}
